import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { lineUnitPrice } from "@/lib/format";
import { trackEvent } from "@/lib/tracker";

const CartContext = createContext(null);
const KEY = "ee_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  // Transient (non-persisted) single-item express checkout buffer
  const [buyNow, setBuyNowState] = useState(null);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const setBuyNow = (product, variant_index = null, quantity = 1) =>
    setBuyNowState({ product, variant_index, quantity: Math.max(1, quantity) });
  const updateBuyNowQty = (qty) =>
    setBuyNowState((prev) => (prev ? { ...prev, quantity: Math.max(1, qty) } : prev));
  const clearBuyNow = () => setBuyNowState(null);

  const addItem = (product, variant_index = null, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex(
        (i) => i.product.id === product.id && i.variant_index === variant_index,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, { product, variant_index, quantity }];
    });
    setOpen(true);
    trackEvent("cart_add", { product_slug: product.slug, label: product.name, value: product.price });
  };

  const updateQty = (i, qty) =>
    setItems((prev) =>
      prev
        .map((it, idx) => (idx === i ? { ...it, quantity: Math.max(1, qty) } : it))
        .filter((it) => it.quantity > 0),
    );

  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const clear = () => setItems([]);

  const value = useMemo(() => {
    const count = items.reduce((a, b) => a + b.quantity, 0);
    const subtotal = items.reduce((a, b) => a + lineUnitPrice(b) * b.quantity, 0);
    return { items, count, subtotal, addItem, updateQty, removeItem, clear, open, setOpen, buyNow, setBuyNow, updateBuyNowQty, clearBuyNow };
  }, [items, open, buyNow]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
